import type { VisionSettings, VisionTransport } from "@/shared/domain/orgs";
import {
  Collapsible,
  TextInput,
  Toggle,
  ToggleGroup,
} from "@/shared/foundation/primitives";

import styles from "./VisionSettingsEditor.module.css";
import {
  VISION_NUMBER_KEYS,
  VISION_NUMBER_LABELS,
  VISION_TRANSPORT_LABELS,
  type VisionDraft,
} from "./visionSettingsForm";

type ImagesChoice = "default" | "on" | "off";

type TransportChoice = VisionTransport | "default";

const TRANSPORTS: readonly VisionTransport[] = ["converse", "openai_invoke"];

interface VisionSettingsEditorProps {
  value: VisionDraft;
  onChange: (next: VisionDraft) => void;
  /** The model family's vision settings; `undefined` while unknown (a new model). */
  defaults: VisionSettings | undefined;
  disabled?: boolean;
}

/**
 * A collapsed "Vision" section: how the Vision role (video analysis) sends
 * this model frames. Every setting defaults to the model family's: a
 * three-way Default / On / Off for image support, a transport, and blank
 * numbers whose placeholders show the family's value.
 */
export function VisionSettingsEditor({
  value,
  onChange,
  defaults,
  disabled = false,
}: VisionSettingsEditorProps) {
  const set = (patch: Partial<VisionDraft>) => onChange({ ...value, ...patch });

  const imagesDefault =
    defaults === undefined
      ? "Default (by model family)"
      : `Default (${defaults.supports_images ? "on" : "off"})`;
  const transportDefault =
    defaults === undefined
      ? "Default (by model family)"
      : `Default (${VISION_TRANSPORT_LABELS[defaults.transport]})`;
  let imagesChoice: ImagesChoice = "default";
  if (value.supports_images !== null) {
    imagesChoice = value.supports_images ? "on" : "off";
  }

  return (
    <Collapsible.Root className={styles.root}>
      <Collapsible.Trigger aria-label="Vision">Vision</Collapsible.Trigger>
      <Collapsible.Panel>
        <div className={styles.panel}>
          <p className={styles.note}>
            How video analysis sends this model frames. Only a model that takes
            images can run the Vision role. Blank follows the model family.
          </p>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Takes images</span>
            <ToggleGroup<ImagesChoice>
              size="sm"
              aria-label="Takes images"
              value={[imagesChoice]}
              onValueChange={([next]) => {
                // Pressing the current option again would clear the group; ignore it.
                if (next !== undefined) {
                  set({
                    supports_images: next === "default" ? null : next === "on",
                  });
                }
              }}
              disabled={disabled}
            >
              <Toggle<ImagesChoice> value="default">{imagesDefault}</Toggle>
              <Toggle<ImagesChoice> value="on">On</Toggle>
              <Toggle<ImagesChoice> value="off">Off</Toggle>
            </ToggleGroup>
          </div>
          {/* A toggle group, not a Select: the Select's popup stacks under
              the foundation Dialog this editor sits in. */}
          <div className={styles.row}>
            <span className={styles.rowLabel}>Transport</span>
            <ToggleGroup<TransportChoice>
              size="sm"
              aria-label="Transport"
              value={[value.transport ?? "default"]}
              onValueChange={([next]) => {
                if (next !== undefined) {
                  set({ transport: next === "default" ? null : next });
                }
              }}
              disabled={disabled}
            >
              <Toggle<TransportChoice> value="default">
                {transportDefault}
              </Toggle>
              {TRANSPORTS.map((transport) => (
                <Toggle<TransportChoice> key={transport} value={transport}>
                  {VISION_TRANSPORT_LABELS[transport]}
                </Toggle>
              ))}
            </ToggleGroup>
          </div>
          <div className={styles.numbers}>
            {VISION_NUMBER_KEYS.map((key) => {
              let placeholder = "Family default";
              if (defaults !== undefined) {
                const fallback = defaults[key];
                placeholder = fallback === null ? "No limit" : String(fallback);
              }
              return (
                <label key={key} className={styles.field}>
                  <span className={styles.fieldLabel}>
                    {VISION_NUMBER_LABELS[key]}
                  </span>
                  <TextInput
                    aria-label={VISION_NUMBER_LABELS[key]}
                    inputMode="numeric"
                    placeholder={placeholder}
                    value={value[key]}
                    onChange={(event) => set({ [key]: event.target.value })}
                    disabled={disabled}
                  />
                </label>
              );
            })}
          </div>
        </div>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}
